(() => {
  "use strict";

  const CONFIG = Object.freeze({
    FLAG_ID: "biontech-chat-widget-loaded",
    STORAGE_KEY: "biontech_chat_history",
    MAX_HISTORY: 6,
    MAX_RETRIES: 15,
    RETRY_INTERVAL_MS: 200,
    SELECTORS: {
      ROOT: "#biontech-chat-root, .biontech-chat-container",
      CONTAINER: "biontech-widget-container",
      TOGGLE_BTN: "biontech-toggle-btn",
      CLOSE_BTN: "biontech-close-btn",
      MESSAGES: "biontech-messages",
      FORM: "biontech-form",
      INPUT: "biontech-input",
      SEND_BTN: "biontech-send-btn",
    },
    DEV_HOSTS: ["localhost", "127.0.0.1"],
    DEV_ENDPOINT: "http://localhost:3000/api/chat",
    PROD_ENDPOINT: "/apps/biontech-chat/api/chat",
  });

  // Prevenir doble inicialización si el script se carga múltiples veces
  if (document.getElementById(CONFIG.FLAG_ID)) return;

  const flagElement = document.createElement("div");
  flagElement.id = CONFIG.FLAG_ID;
  flagElement.style.display = "none";
  document.body.appendChild(flagElement);

  const storageService = {
    getHistory() {
      try {
        const history = sessionStorage.getItem(CONFIG.STORAGE_KEY);
        return history ? JSON.parse(history) : [];
      } catch (error) {
        console.warn("⚠️ sessionStorage no disponible:", error);
        return [];
      }
    },
    saveHistory(history) {
      try {
        const slicedHistory = history.slice(-CONFIG.MAX_HISTORY);
        sessionStorage.setItem(CONFIG.STORAGE_KEY, JSON.stringify(slicedHistory));
        return slicedHistory;
      } catch (error) {
        return history;
      }
    },
  };

  const utils = {
    escapeHTML(str) {
      if (!str) return "";
      return String(str)
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&#039;");
    },
    parseMarkdown(text) {
      if (!text) return "";
      let html = this.escapeHTML(text);
      html = html.replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>");
      html = html.replace(/\*([^*]+)\*/g, "<em>$1</em>");
      html = html.replace(/`([^`]+)`/g, "<code>$1</code>");
      return html.replace(/\[([^\]]+)\]\(([^)]+)\)/g, (match, linkText, url) => {
        const trimmedUrl = url.trim();
        if (/^(https?:\/\/|\/|mailto:)/i.test(trimmedUrl)) {
          return `<a href="${trimmedUrl}" target="_blank" rel="noopener noreferrer" class="biontech-link">${linkText}</a>`;
        }
        return linkText;
      });
    },
  };

  const apiService = {
    async sendMessage(question, history) {
      const isDev = CONFIG.DEV_HOSTS.includes(window.location.hostname);
      const endpoint = isDev ? CONFIG.DEV_ENDPOINT : CONFIG.PROD_ENDPOINT;

      const response = await fetch(endpoint, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({ pregunta: question, historial: history }),
      });

      if (!response.ok) {
        throw new Error(`HTTP Error ${response.status}: ${response.statusText}`);
      }

      return await response.json();
    },
  };

  class BiontechChatWidget {
    constructor() {
      this.historial = storageService.getHistory();
      this.isProcessing = false;
      this.elements = {};
      this.retryCount = 0;
    }

    init() {
      const rootEl = document.querySelector(CONFIG.SELECTORS.ROOT);

      if (!rootEl) {
        if (this.retryCount < CONFIG.MAX_RETRIES) {
          this.retryCount++;
          setTimeout(() => this.init(), CONFIG.RETRY_INTERVAL_MS);
        } else {
          console.warn("⚠️ Biontech Chat: No se encontró el contenedor del chat en el DOM.");
        }
        return;
      }

      const botName = rootEl.getAttribute("data-bot-name") || "Asistente Biontech";
      const welcomeMsg =
        rootEl.getAttribute("data-welcome-msg") ||
        "¡Hola! 👋 Soy el asistente con Inteligencia Artificial de Biontech. ¿En qué puedo ayudarte hoy?";
      const primaryColor = rootEl.getAttribute("data-primary-color") || "#0070f3";

      document.documentElement.style.setProperty("--biontech-primary-color", primaryColor);

      this.renderTemplate(rootEl, botName, welcomeMsg);
      this.cacheElements();
      this.restoreHistory();
      this.bindEvents();
    }

    renderTemplate(rootEl, botName, welcomeMsg) {
      const escapedBotName = utils.escapeHTML(botName);

      rootEl.innerHTML = `
        <div id="${CONFIG.SELECTORS.CONTAINER}" class="biontech-closed">
          <button id="${CONFIG.SELECTORS.TOGGLE_BTN}" aria-expanded="false" aria-controls="biontech-chat-box" aria-label="Abrir chat">
            💬 Chat ${escapedBotName}
          </button>
          <div id="biontech-chat-box" aria-hidden="true">
            <div class="biontech-header">
              <div class="biontech-header-title">
                <span class="biontech-bot-name">${escapedBotName}</span>
                <span class="biontech-ai-badge">⚡ Impulsado por IA</span>
              </div>
              <button id="${CONFIG.SELECTORS.CLOSE_BTN}" aria-label="Cerrar chat">&times;</button>
            </div>
            <div id="${CONFIG.SELECTORS.MESSAGES}" aria-live="polite">
              <div class="biontech-msg biontech-msg-bot">${utils.parseMarkdown(welcomeMsg)}</div>
            </div>
            <form id="${CONFIG.SELECTORS.FORM}" class="biontech-input-area">
              <input type="text" id="${CONFIG.SELECTORS.INPUT}" placeholder="Escribe tu consulta..." aria-label="Escribe tu consulta" autocomplete="off" />
              <button type="submit" id="${CONFIG.SELECTORS.SEND_BTN}" aria-label="Enviar mensaje">Enviar</button>
            </form>
            <div class="biontech-ai-disclaimer">Las respuestas son generadas por un modelo de Inteligencia Artificial.</div>
          </div>
        </div>
      `;
    }

    cacheElements() {
      this.elements = {
        container: document.getElementById(CONFIG.SELECTORS.CONTAINER),
        toggleBtn: document.getElementById(CONFIG.SELECTORS.TOGGLE_BTN),
        closeBtn: document.getElementById(CONFIG.SELECTORS.CLOSE_BTN),
        messages: document.getElementById(CONFIG.SELECTORS.MESSAGES),
        form: document.getElementById(CONFIG.SELECTORS.FORM),
        input: document.getElementById(CONFIG.SELECTORS.INPUT),
        sendBtn: document.getElementById(CONFIG.SELECTORS.SEND_BTN),
      };
    }

    bindEvents() {
      this.elements.toggleBtn.addEventListener("click", () => this.toggleChat());
      this.elements.closeBtn.addEventListener("click", () => this.closeChat());
      this.elements.form.addEventListener("submit", (e) => {
        e.preventDefault();
        this.handleSubmit();
      });
      document.addEventListener("keydown", (e) => {
        if (e.key === "Escape" && this.elements.container?.classList.contains("biontech-open")) {
          this.closeChat();
        }
      });
    }

    toggleChat() {
      const isOpen = this.elements.container.classList.toggle("biontech-open");
      this.elements.toggleBtn.setAttribute("aria-expanded", String(isOpen));
      this.elements.container.querySelector("#biontech-chat-box").setAttribute("aria-hidden", String(!isOpen));

      if (isOpen) {
        setTimeout(() => this.elements.input.focus(), 100);
        this.scrollToBottom();
      } else {
        this.elements.toggleBtn.focus();
      }
    }

    closeChat() {
      this.elements.container.classList.remove("biontech-open");
      this.elements.toggleBtn.setAttribute("aria-expanded", "false");
      this.elements.container.querySelector("#biontech-chat-box").setAttribute("aria-hidden", "true");
      this.elements.toggleBtn.focus();
    }

    restoreHistory() {
      if (!this.historial.length) return;

      this.historial.forEach((item) => {
        this.addMessageDOM(item.text, item.role === "user" ? "user" : "bot");
      });
      this.scrollToBottom();
    }

    setLoading(isLoading) {
      this.isProcessing = isLoading;
      this.elements.sendBtn.disabled = isLoading;
      this.elements.input.disabled = isLoading;
    }

    scrollToBottom() {
      requestAnimationFrame(() => {
        if (this.elements.messages) {
          this.elements.messages.scrollTop = this.elements.messages.scrollHeight;
        }
      });
    }

    generateUniqueId() {
      if (typeof crypto !== "undefined" && crypto.randomUUID) {
        return `msg-${crypto.randomUUID()}`;
      }
      return `msg-${Date.now()}-${Math.random().toString(36).substring(2, 6)}`;
    }

    addMessageDOM(text, role) {
      const msgEl = document.createElement("div");
      const msgId = this.generateUniqueId();
      msgEl.id = msgId;

      if (role === "bot-loading") {
        msgEl.className = "biontech-msg biontech-msg-bot biontech-msg-bot-loading";
        msgEl.innerHTML =
          '<span class="biontech-typing-dot"></span><span class="biontech-typing-dot"></span><span class="biontech-typing-dot"></span>';
      } else {
        msgEl.className = `biontech-msg biontech-msg-${role}`;
        msgEl.innerHTML = utils.parseMarkdown(text);
      }

      this.elements.messages.appendChild(msgEl);
      this.scrollToBottom();
      return msgId;
    }

    async handleSubmit(overridePrompt = null) {
      if (this.isProcessing) return;

      const promptText = overridePrompt || this.elements.input.value.trim();
      if (!promptText) return;

      if (!overridePrompt) {
        this.elements.input.value = "";
        this.addMessageDOM(promptText, "user");
      }

      const loadingId = this.addMessageDOM("", "bot-loading");
      this.setLoading(true);

      try {
        const responseData = await apiService.sendMessage(promptText, this.historial);
        this.removeElement(loadingId);

        const botAnswer = responseData.respuesta_bot || "No se pudo obtener una respuesta válida.";
        this.addMessageDOM(botAnswer, "bot");

        this.historial.push({ role: "user", text: promptText });
        this.historial.push({ role: "assistant", text: botAnswer });
        this.historial = storageService.saveHistory(this.historial);
      } catch (error) {
        console.error("🔴 Error de red en el widget:", error);
        this.removeElement(loadingId);
        this.addErrorFallback("Ocurrió un error de conexión con el servidor.", promptText);
      } finally {
        this.setLoading(false);
        if (this.elements.container.classList.contains("biontech-open")) {
          this.elements.input.focus();
        }
      }
    }

    addErrorFallback(errorMessage, originalPrompt) {
      const errorEl = document.createElement("div");
      errorEl.className = "biontech-msg biontech-msg-bot biontech-msg-error";

      const mailSubject = encodeURIComponent("Consulta desde Chatbot Biontech");
      const mailBody = encodeURIComponent(`Hola equipo Biontech,\n\nTengo la siguiente consulta:\n"${originalPrompt}"`);
      const mailtoUrl = `mailto:info@biontechworld.com?subject=${mailSubject}&body=${mailBody}`;
      const escapedPrompt = utils.escapeHTML(originalPrompt);

      errorEl.innerHTML = `
        <span>${utils.escapeHTML(errorMessage)}</span>
        <div style="font-size: 11px; opacity: 0.8; margin-top: 4px;">Tu consulta: "${escapedPrompt}"</div>
        <div class="biontech-fallback-actions">
          <button class="biontech-retry-btn">🔄 Reintentar</button>
          <a href="${mailtoUrl}" class="biontech-support-btn" target="_blank" rel="noopener noreferrer">✉️ Soporte por email</a>
        </div>
      `;

      this.elements.messages.appendChild(errorEl);
      this.scrollToBottom();

      errorEl.querySelector(".biontech-retry-btn").addEventListener("click", () => {
        errorEl.remove();
        this.handleSubmit(originalPrompt);
      });
    }

    removeElement(elementId) {
      const el = document.getElementById(elementId);
      if (el) el.remove();
    }
  }

  const widget = new BiontechChatWidget();

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => widget.init());
  } else {
    widget.init();
  }
})();